//src/context/CartContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { CartItem } from '../utils/types'; // Adjust based on your setup
// import { useSettings } from '../context/SettingsContext';

interface CartContextType {
    cartItems: CartItem[];
    addToCart: (item: CartItem, isEditMode?: boolean) => void; // Add isEditMode as optional
    updateThisCart: (item: CartItem) => void; // Function for updating existing items
    updateQuantity: (productKey: string, newQuantity: number) => void;
    removeFromCart: (productKey: string) => void; // Accept productKey (string)
    clearCart: () => void;
    setCartItems: React.Dispatch<React.SetStateAction<CartItem[]>>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    const [cartItems, setCartItems] = useState<CartItem[]>(() => {
        const savedItems = localStorage.getItem('cartItems');
        return savedItems ? JSON.parse(savedItems) : [];
    });


    useEffect(() => {
        localStorage.setItem('cartItems', JSON.stringify(cartItems));
    }, [cartItems]);

    //src/context/CartContext.tsx
    // Function to add a new item or update quantity if it exists
    const addToCart = (item: CartItem, isEditMode = false) => {
        
        setCartItems((prevItems) => {
            const existingItem = prevItems.find(cartItem => cartItem.productKey === item.productKey);

            if (existingItem) {
                if (isEditMode) {
                    // In edit mode, fully replace the item with the new one (with new quantity and totalPrice)
                    return prevItems.map(cartItem =>
                        cartItem.productKey === item.productKey
                            ? { ...cartItem, ...item } // Replace with the updated item details
                            : cartItem
                    );
                } else {
                    // If not in edit mode, just increase the quantity and recalculate totalPrice
                    return prevItems.map(cartItem =>
                        cartItem.productKey === item.productKey
                            ? {
                                ...cartItem,
                                quantity: cartItem.quantity + item.quantity, // Increment quantity
                                totalPrice: (cartItem.totalPrice / cartItem.quantity) * (cartItem.quantity + item.quantity) // Update total price based on new quantity
                            }
                            : cartItem
                    );
                }
            } else {
                // Add new item if it's not in the cart
                return [...prevItems, item];
            }
        });
    };

    const updateQuantity = (productKey: string, newQuantity: number) => {

        console.log('updating', cartItems, productKey, newQuantity);

        setCartItems((prevItems) =>
            prevItems
                .map((item) =>
                    item.productKey === productKey
                        ? {
                            ...item,
                            quantity: newQuantity < 1 ? 1 : newQuantity, // Ensure minimum quantity of 1
                            totalPrice: item.price * (newQuantity < 1 ? 1 : newQuantity), // Adjust total price
                        }
                        : item
                )
                .filter((item) => item.quantity > 0) // Remove items with quantity 0
        );
    };

    // Function to update an item directly when editing
    const updateThisCart = (updatedItem: CartItem) => {
        setCartItems((prevItems) =>
            prevItems
                .map((cartItem) =>
                    cartItem.productKey === updatedItem.productKey
                        ? {
                            ...cartItem,
                            options: updatedItem.options, // Update options specifically
                            quantity: updatedItem.quantity < 1 ? 1 : updatedItem.quantity, // Ensure minimum quantity of 1
                            totalPrice: updatedItem.price * (updatedItem.quantity < 1 ? 1 : updatedItem.quantity), // Adjust total price
                        }
                        : cartItem // Leave other items unchanged
                )
                .filter((item) => item.quantity > 0) // Remove items with quantity 0
        );
    };

    // Function to update an item directly when editing
    // const updateThisCart = (updatedItem: CartItem) => {
    //     console.log('updatedItem', updatedItem);
    //     setCartItems((prevItems) =>
    //         prevItems.map((cartItem) =>
    //             cartItem.productKey === updatedItem.productKey
    //                 ? {
    //                     ...cartItem,
    //                     options: updatedItem.options ? updatedItem.options : cartItem.options, // Update options if provided
    //                     quantity: updatedItem.quantity, // Update quantity if changed
    //                     totalPrice: updatedItem.totalPrice, // Update total price to reflect any changes
    //                 }
    //                 : cartItem // Leave other items unchanged
    //         )
    //     );
    // };

    // Function to remove an item from the cart
    const removeFromCart = (productKey: string) => {
        setCartItems((prevItems) => prevItems.filter(cartItem => cartItem.productKey !== productKey));
    };


    const clearCart = () => {
        setCartItems([]); // Clears all cart items
    };

    return (
        <CartContext.Provider value={{
            cartItems, addToCart, updateThisCart, updateQuantity, removeFromCart, clearCart, setCartItems
        }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error("useCart must be used within a CartProvider");
    }
    return context;
};